import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import { Check, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/cn';

const ITEM_SELECTOR = '[role="menuitem"]:not([aria-disabled="true"]), [role="menuitemradio"]:not([aria-disabled="true"])';

export interface MenuItem {
  label: string;
  icon?: LucideIcon;
  onSelect?: () => void;
  /** Navigates instead of calling onSelect. */
  to?: string;
  tone?: 'default' | 'danger';
  disabled?: boolean;
  /** Marks the current choice in a single-select menu (e.g. the active center). */
  checked?: boolean;
  /** Secondary line under the label. */
  description?: string;
}

/** Non-interactive group label inside a menu. */
export interface MenuHeading {
  heading: string;
}

export interface MenuProps {
  /** The visible trigger content; the button wrapper is provided. */
  trigger: ReactNode;
  /** Accessible name of the trigger button. */
  label: string;
  items: readonly (MenuItem | MenuHeading | 'separator')[];
  align?: 'start' | 'end';
  /** Where the panel opens relative to the trigger. */
  side?: 'bottom' | 'top';
  triggerClassName?: string;
  header?: ReactNode;
}

/** Small action menu (row actions, user menu). Arrow keys move, Escape closes and restores focus. */
export function Menu({ trigger, label, items, align = 'end', side = 'bottom', triggerClassName, header }: MenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: PointerEvent): void => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    panelRef.current?.querySelector<HTMLElement>(ITEM_SELECTOR)?.focus();
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const close = (restoreFocus = true): void => {
    setOpen(false);
    if (restoreFocus) {
      buttonRef.current?.focus();
    }
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      close();
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return;
    }
    event.preventDefault();
    const nodes = Array.from(panelRef.current?.querySelectorAll<HTMLElement>(ITEM_SELECTOR) ?? []);
    const index = nodes.indexOf(document.activeElement as HTMLElement);
    const next = event.key === 'ArrowDown' ? (index + 1) % nodes.length : (index - 1 + nodes.length) % nodes.length;
    nodes[next]?.focus();
  };

  const itemClass = (item: MenuItem): string =>
    cn(
      'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm transition-colors',
      item.tone === 'danger' ? 'text-red-700 hover:bg-red-50 focus:bg-red-50' : 'text-slate-700 hover:bg-slate-100 focus:bg-slate-100',
      item.disabled && 'pointer-events-none opacity-50',
    );

  return (
    <div ref={rootRef} role="presentation" className="relative" onKeyDown={onKeyDown}>
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((value) => !value)}
        className={triggerClassName}
      >
        {trigger}
      </button>
      {open && (
        <div
          ref={panelRef}
          id={menuId}
          role="menu"
          aria-label={label}
          className={cn(
            'absolute z-50 min-w-52 animate-scale-in rounded-xl border border-slate-200 bg-white p-1.5 shadow-pop',
            align === 'end' ? 'right-0' : 'left-0',
            side === 'bottom' ? 'top-full mt-1.5' : 'bottom-full mb-1.5',
          )}
        >
          {header && <div className="border-b border-slate-100 px-2.5 pb-2 pt-1.5">{header}</div>}
          {items.map((item, index) => {
            if (item === 'separator') {
              return <div key={`sep-${index}`} role="separator" className="my-1 h-px bg-slate-100" />;
            }
            if ('heading' in item) {
              return (
                <p key={`heading-${item.heading}`} role="presentation" className="px-2.5 pb-1 pt-2 text-2xs font-semibold uppercase tracking-wider text-slate-400">
                  {item.heading}
                </p>
              );
            }
            const Icon = item.icon;
            const content = (
              <>
                {Icon && <Icon className="size-4 shrink-0 text-slate-400" aria-hidden />}
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{item.label}</span>
                  {item.description && <span className="block truncate text-xs text-slate-500">{item.description}</span>}
                </span>
                {item.checked && <Check className="size-4 shrink-0 text-brand-700" aria-hidden />}
              </>
            );
            if (item.to) {
              return (
                <Link key={item.label} role="menuitem" to={item.to} aria-disabled={item.disabled || undefined} onClick={() => close(false)} className={itemClass(item)}>
                  {content}
                </Link>
              );
            }
            return (
              <button
                key={item.label}
                type="button"
                role={item.checked !== undefined ? 'menuitemradio' : 'menuitem'}
                aria-checked={item.checked}
                aria-disabled={item.disabled || undefined}
                onClick={() => {
                  close();
                  item.onSelect?.();
                }}
                className={itemClass(item)}
              >
                {content}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
